import { CartLinesUpdateEvent } from '@shopify/events';

const LEGACY_STORAGE_KEY = 'khushi:wishlist:v1';
const GUEST_STORAGE_KEY = 'khushi:wishlist:guest:v1';
const CUSTOMER_STORAGE_PREFIX = 'khushi:wishlist:customer:';
const LEGACY_MIGRATION_KEY = 'khushi:wishlist:legacy-migrated:v1';
const STORAGE_VERSION = 1;
const DRAWER_ID = 'khushi-wishlist-drawer';
const PRODUCT_CACHE = new Map();
const METADATA_REFRESH_TTL_MS = 2 * 60 * 1000;
const KHUSHI_DOM_CHANGED_EVENT = 'khushi:dom:changed';

const HEART = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class KhushiWishlist {
  constructor() {
    this.customerId = this.resolveCustomerId();
    this.storageKey = this.customerId ? `${CUSTOMER_STORAGE_PREFIX}${this.customerId}:v1` : GUEST_STORAGE_KEY;
    this.drawer = null;
    this.toastTimer = null;
    this.drawerAction = Promise.resolve();
    this.metadataRefreshPromise = null;
    this.lastMetadataRefreshAt = 0;

    this.migrateStorage();
    this.items = this.read();
    this.bindStorageSync();
    this.init();
  }

  resolveCustomerId() {
    const value = document.querySelector('[data-khushi-wishlist-open]')?.dataset.khushiCustomerId || '';
    return String(value).trim();
  }

  normalizeItems(items) {
    if (!Array.isArray(items)) return [];
    return items.filter((item) => item && item.productId).map((item) => ({ ...item, productId: String(item.productId), variantId: item.variantId ? String(item.variantId) : '' }));
  }

  readKey(key) {
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || '{}');
      return this.normalizeItems(parsed.items);
    } catch {
      return [];
    }
  }

  mergeItems(...lists) {
    const seen = new Set();
    const merged = [];
    for (const list of lists) {
      for (const item of this.normalizeItems(list)) {
        if (seen.has(item.productId)) continue;
        seen.add(item.productId);
        merged.push(item);
      }
    }
    return merged;
  }

  writeKey(key, items) {
    localStorage.setItem(key, JSON.stringify({ version: STORAGE_VERSION, items: this.normalizeItems(items) }));
  }

  migrateStorage() {
    try {
      if (localStorage.getItem(LEGACY_MIGRATION_KEY) !== '1') {
        const guest = this.readKey(GUEST_STORAGE_KEY);
        const legacy = this.readKey(LEGACY_STORAGE_KEY);
        const mergedGuest = this.mergeItems(guest, legacy);
        if (mergedGuest.length || localStorage.getItem(GUEST_STORAGE_KEY) !== null) this.writeKey(GUEST_STORAGE_KEY, mergedGuest);
        localStorage.setItem(LEGACY_MIGRATION_KEY, '1');
      }

      if (this.customerId) {
        const account = this.readKey(this.storageKey);
        const guest = this.readKey(GUEST_STORAGE_KEY);
        const merged = this.mergeItems(account, guest);
        if (merged.length || localStorage.getItem(this.storageKey) !== null || guest.length) this.writeKey(this.storageKey, merged);
        if (guest.length) localStorage.removeItem(GUEST_STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Khushi Wishlist: storage migration unavailable', error);
    }
  }

  read() {
    return this.readKey(this.storageKey);
  }

  save() {
    this.writeKey(this.storageKey, this.items);
    this.syncUI();
    this.renderDrawer();
    window.dispatchEvent(new CustomEvent('khushi:wishlist:changed', { detail: { items: this.items, customerId: this.customerId || null, storageKey: this.storageKey } }));
  }

  bindStorageSync() {
    window.addEventListener('storage', (event) => {
      if (event.key !== this.storageKey) return;
      this.items = this.read();
      this.lastMetadataRefreshAt = 0;
      this.syncUI();
      this.renderDrawer();
      window.dispatchEvent(new CustomEvent('khushi:wishlist:changed', { detail: { items: this.items, customerId: this.customerId || null, storageKey: this.storageKey } }));
    });
  }

  has(productId) {
    return this.items.some((item) => item.productId === String(productId));
  }

  remove(productId) {
    this.items = this.items.filter((item) => item.productId !== String(productId));
    this.save();
  }

  upsert(item) {
    const id = String(item.productId);
    const existing = this.items.findIndex((entry) => entry.productId === id);
    if (existing > -1) this.items[existing] = { ...this.items[existing], ...item, productId: id };
    else this.items.unshift({ ...item, productId: id });
    this.save();
  }

  async init() {
    this.bindHeader();
    this.bindDrawerStateSync();
    this.injectCardButtons(document);

    window.addEventListener(KHUSHI_DOM_CHANGED_EVENT, (event) => this.injectCardButtons(event.detail?.root || document));
    document.addEventListener('shopify:section:load', (event) => {
      this.bindHeader();
      this.injectCardButtons(event.target);
    });

    customElements.whenDefined('theme-drawer').then(() => {
      this.ensureDrawer();
      this.syncUI();
    });
    this.syncUI();
  }

  bindHeader() {
    document.querySelectorAll('[data-khushi-wishlist-open]').forEach((button) => {
      if (button.dataset.khushiBound) return;
      button.dataset.khushiBound = '1';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle();
      });
    });
  }

  bindDrawerStateSync() {
    if (this.drawerStateSyncBound) return;
    this.drawerStateSyncBound = true;
    document.addEventListener('theme-drawer:open', () => requestAnimationFrame(() => this.syncUI()));
    document.addEventListener('theme-drawer:close', () => requestAnimationFrame(() => this.syncUI()));
  }

  injectCardButtons(root) {
    const cards = [];
    if (root?.matches?.('product-card[data-product-id]')) cards.push(root);
    root?.querySelectorAll?.('product-card[data-product-id]').forEach((card) => cards.push(card));

    for (const card of cards) {
      if (card.querySelector(':scope > .khushi-wishlist-card-button')) continue;
      const link = card.querySelector('.product-card__link[href],a[href*="/products/"]');
      if (!link) continue;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'khushi-wishlist-card-button';
      button.dataset.productId = String(card.dataset.productId || '');
      button.dataset.productUrl = link.href;
      button.setAttribute('aria-label', 'Add to wishlist');
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = HEART;
      button.addEventListener('click', (event) => this.toggleFromButton(event, button, card));

      const content = card.querySelector('.product-card__content');
      card.insertBefore(button, content || card.firstChild);
      this.updateButton(button);
    }
  }

  updateButton(button) {
    const active = this.has(button.dataset.productId);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  syncUI() {
    document.querySelectorAll('.khushi-wishlist-card-button,.khushi-wishlist-product-button').forEach((button) => this.updateButton(button));
    const count = this.items.length;
    const displayCount = count > 99 ? '99+' : String(count);

    document.querySelectorAll('[data-khushi-wishlist-count]').forEach((element) => {
      element.textContent = displayCount;
      element.classList.toggle('hidden', count === 0);
    });
    document.querySelectorAll('[data-khushi-wishlist-bubble]').forEach((element) => {
      element.classList.toggle('visually-hidden', count === 0);
      if (count <= 99) element.setAttribute('data-maintain-ratio', '');
      else element.removeAttribute('data-maintain-ratio');
    });
    document.querySelectorAll('[data-khushi-wishlist-icon]').forEach((element) => element.classList.toggle('header-actions__cart-icon--has-cart', count > 0));

    const open = Boolean(this.drawer?.hasAttribute('open'));
    document.querySelectorAll('[data-khushi-wishlist-open]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
  }

  getVariantId(context, url, product) {
    let id = '';
    try {
      id = context.closest?.('product-card')?.getSelectedVariantId?.() || '';
    } catch {}

    if (!id) id = context.querySelector?.('input[name="id"]:checked,input[name="id"],input[data-variant-id]:checked')?.value || context.querySelector?.('input[data-variant-id]:checked')?.dataset.variantId || '';
    if (!id) {
      const parsed = new URL(url, location.origin);
      id = parsed.searchParams.get('variant') || '';
    }
    if (!id) id = product.variants?.find((variant) => variant.available)?.id || product.variants?.[0]?.id || '';
    return String(id);
  }

  async toggleFromButton(event, button, context) {
    event.preventDefault();
    event.stopPropagation();
    const productId = String(button.dataset.productId || '');

    if (productId && this.has(productId)) {
      this.remove(productId);
      this.toast('Removed from wishlist');
      return;
    }

    button.disabled = true;
    try {
      const product = await this.fetchProduct(button.dataset.productUrl);
      const id = productId || String(product.id);
      const variantId = this.getVariantId(context, button.dataset.productUrl, product);
      const variant = product.variants?.find((entry) => String(entry.id) === variantId) || product.variants?.find((entry) => entry.available) || product.variants?.[0];

      this.upsert({
        productId: id,
        variantId: String(variant?.id || variantId),
        handle: product.handle || '',
        url: this.cleanProductUrl(button.dataset.productUrl),
        title: product.title || '',
        variantTitle: variant?.title && variant.title !== 'Default Title' ? variant.title : '',
        image: variant?.featured_image?.src || product.featured_image || product.images?.[0] || '',
        price: Number(variant?.price ?? product.price ?? 0),
        available: Boolean(variant?.available),
      });
      this.toast('Added to wishlist');
    } catch (error) {
      console.error(error);
      this.toast('Could not add to wishlist');
    } finally {
      button.disabled = false;
      this.updateButton(button);
    }
  }

  cleanProductUrl(url) {
    const parsed = new URL(url, location.origin);
    parsed.search = '';
    parsed.hash = '';
    return parsed.pathname;
  }

  productJsonUrl(url) {
    const parsed = new URL(url, location.origin);
    parsed.hash = '';
    parsed.search = '';
    if (!parsed.pathname.endsWith('.js')) parsed.pathname = parsed.pathname.replace(/\/$/, '') + '.js';
    return parsed.href;
  }

  async fetchProduct(url, { fresh = false } = {}) {
    const key = this.productJsonUrl(url);
    if (fresh) PRODUCT_CACHE.delete(key);
    if (PRODUCT_CACHE.has(key)) return PRODUCT_CACHE.get(key);

    const request = fetch(key, { headers: { Accept: 'application/json' } }).then(async (response) => {
      if (!response.ok) throw new Error('Product request failed');
      return response.json();
    });
    PRODUCT_CACHE.set(key, request);

    try {
      return await request;
    } catch (error) {
      PRODUCT_CACHE.delete(key);
      throw error;
    }
  }

  async refreshItemsMetadata({ force = false } = {}) {
    if (!this.items.length) return;
    if (!force && Date.now() - this.lastMetadataRefreshAt < METADATA_REFRESH_TTL_MS) return;
    if (this.metadataRefreshPromise) return this.metadataRefreshPromise;

    this.metadataRefreshPromise = Promise.all(this.items.map(async (item) => {
      const url = item.url || (item.handle ? `/products/${item.handle}` : '');
      if (!url) return item;

      try {
        const product = await this.fetchProduct(url, { fresh: true });
        const storedVariantId = String(item.variantId || '');
        const exactVariant = storedVariantId ? product.variants?.find((variant) => String(variant.id) === storedVariantId) : null;

        if (storedVariantId && !exactVariant) {
          return { ...item, handle: product.handle || item.handle, url: this.cleanProductUrl(url), title: product.title || item.title, image: product.featured_image || product.images?.[0] || item.image, available: false };
        }

        const variant = exactVariant || product.variants?.find((entry) => entry.available) || product.variants?.[0];
        if (!variant) {
          return { ...item, handle: product.handle || item.handle, url: this.cleanProductUrl(url), title: product.title || item.title, image: product.featured_image || product.images?.[0] || item.image, available: false };
        }

        return {
          ...item,
          productId: String(product.id || item.productId),
          variantId: String(variant.id || item.variantId || ''),
          handle: product.handle || item.handle,
          url: this.cleanProductUrl(url),
          title: product.title || item.title,
          variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : '',
          image: variant.featured_image?.src || product.featured_image || product.images?.[0] || item.image,
          price: Number(variant.price ?? product.price ?? item.price ?? 0),
          available: Boolean(variant.available),
        };
      } catch {
        return item;
      }
    }))
      .then((updatedItems) => {
        this.lastMetadataRefreshAt = Date.now();
        const normalized = this.normalizeItems(updatedItems);
        if (JSON.stringify(normalized) === JSON.stringify(this.items)) return;
        this.items = normalized;
        this.writeKey(this.storageKey, this.items);
        this.syncUI();
        this.renderDrawer();
        window.dispatchEvent(new CustomEvent('khushi:wishlist:refreshed', { detail: { items: this.items, customerId: this.customerId || null } }));
      })
      .finally(() => {
        this.metadataRefreshPromise = null;
      });

    return this.metadataRefreshPromise;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: window.Shopify?.currency?.active || 'INR', maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100);
    } catch {
      return `₹${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  ensureDrawer() {
    const existing = document.getElementById(DRAWER_ID);
    if (existing?.matches?.('theme-drawer[data-khushi-wishlist-drawer]')) {
      existing.setAttribute('data-drawer-group', 'commerce');
      existing.setAttribute('no-persist', '');
      this.drawer = existing;
      this.renderDrawer();
      return;
    }
    if (existing) existing.remove();

    const element = document.createElement('theme-drawer');
    element.id = DRAWER_ID;
    element.setAttribute('data-khushi-wishlist-drawer', '');
    element.setAttribute('data-drawer-group', 'commerce');
    element.setAttribute('no-persist', '');
    element.innerHTML = `<dialog ref="panel" class="theme-drawer__dialog khushi-wishlist-drawer__dialog" aria-labelledby="khushi-wishlist-heading"><div class="khushi-wishlist-drawer__inner"><div class="theme-drawer__header khushi-wishlist-drawer__header"><h2 id="khushi-wishlist-heading" class="theme-drawer__title h4">Wishlist</h2><span class="theme-drawer__badge" data-khushi-drawer-count aria-hidden="true" data-maintain-ratio>0</span><button type="button" class="theme-drawer__close-button" aria-label="Close wishlist"><span class="svg-wrapper"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span></button></div><div class="khushi-wishlist-drawer__content" data-khushi-wishlist-content></div><div class="khushi-wishlist-drawer__footer" data-khushi-wishlist-footer hidden><button type="button" class="button khushi-wishlist-add-all" data-khushi-add-all>Add all to cart</button></div></div></dialog>`;

    document.body.appendChild(element);
    this.drawer = element;
    element.querySelector('.theme-drawer__close-button').addEventListener('click', () => this.close());
    element.querySelector('[data-khushi-add-all]').addEventListener('click', (event) => this.addAll(event.currentTarget));
    element.querySelector('dialog').addEventListener('close', () => this.syncUI());
    this.renderDrawer();
  }

  toggle() {
    this.drawerAction = this.drawerAction.then(() => this.performToggle()).catch((error) => console.warn('Khushi Wishlist drawer toggle failed', error));
    return this.drawerAction;
  }

  async performToggle() {
    await customElements.whenDefined('theme-drawer');
    this.ensureDrawer();
    if (this.drawer?.hasAttribute('open')) {
      await this.drawer.close?.();
      this.syncUI();
      return;
    }
    this.renderDrawer();
    await this.drawer.open?.();
    requestAnimationFrame(() => this.syncUI());
    void this.refreshItemsMetadata();
  }

  open() {
    this.drawerAction = this.drawerAction.then(async () => {
      await customElements.whenDefined('theme-drawer');
      this.ensureDrawer();
      if (this.drawer?.hasAttribute('open')) {
        void this.refreshItemsMetadata();
        return;
      }
      this.renderDrawer();
      await this.drawer.open?.();
      requestAnimationFrame(() => this.syncUI());
      void this.refreshItemsMetadata();
    }).catch((error) => console.warn('Khushi Wishlist drawer open failed', error));
    return this.drawerAction;
  }

  async close() {
    await customElements.whenDefined('theme-drawer');
    this.ensureDrawer();
    if (this.drawer?.hasAttribute('open')) await this.drawer.close?.();
    this.syncUI();
  }

  renderDrawer() {
    if (!this.drawer) return;
    const content = this.drawer.querySelector('[data-khushi-wishlist-content]');
    const footer = this.drawer.querySelector('[data-khushi-wishlist-footer]');
    const count = this.drawer.querySelector('[data-khushi-drawer-count]');
    if (!content) return;

    if (count) {
      count.textContent = String(this.items.length);
      count.classList.toggle('visually-hidden', this.items.length === 0);
    }
    if (!this.items.length) {
      footer.hidden = true;
      content.innerHTML = `<div class="khushi-wishlist-empty">${HEART}<h3 class="h5">Your wishlist is empty</h3><p>Tap the heart on products you love and they will appear here.</p></div>`;
      return;
    }

    footer.hidden = false;
    content.innerHTML = `<div class="khushi-wishlist-items">${this.items.map((item) => this.itemHtml(item)).join('')}</div>`;
    content.querySelectorAll('[data-khushi-remove]').forEach((button) => button.addEventListener('click', () => this.remove(button.dataset.productId)));
    content.querySelectorAll('[data-khushi-add-one]').forEach((button) => button.addEventListener('click', () => this.addOne(button, button.dataset.productId)));
  }

  esc(value) {
    const replacements = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value ?? '').replace(/[&<>"']/g, (character) => replacements[character]);
  }

  itemHtml(item) {
    const unavailable = item.available === false;
    const image = item.image ? `<a href="${this.esc(item.url)}"><img class="khushi-wishlist-item__image" src="${this.esc(item.image)}" alt="${this.esc(item.title)}" loading="lazy"></a>` : '<div class="khushi-wishlist-item__image"></div>';
    const variant = item.variantTitle ? `<div class="khushi-wishlist-item__variant">${this.esc(item.variantTitle)}</div>` : '';
    const status = unavailable ? '<div class="khushi-wishlist-item__status">Currently unavailable</div>' : '';
    return `<article class="khushi-wishlist-item" data-wishlist-product="${this.esc(item.productId)}">${image}<div class="khushi-wishlist-item__body"><a class="khushi-wishlist-item__title" href="${this.esc(item.url)}">${this.esc(item.title)}</a>${variant}<div class="khushi-wishlist-item__price">${this.esc(this.formatMoney(item.price))}</div>${status}<p class="khushi-wishlist-item__error" data-khushi-error hidden></p><div class="khushi-wishlist-item__actions"><button type="button" class="button button-secondary khushi-wishlist-item__add" data-khushi-add-one data-product-id="${this.esc(item.productId)}" ${unavailable ? 'disabled' : ''}>Add to cart</button><button type="button" class="khushi-wishlist-item__remove" data-khushi-remove data-product-id="${this.esc(item.productId)}">Remove</button></div></div></article>`;
  }

  async addOne(button, productId) {
    const item = this.items.find((entry) => entry.productId === String(productId));
    if (!item?.variantId) return;
    button.disabled = true;
    const oldLabel = button.textContent;
    button.textContent = 'Adding…';
    this.clearItemError(productId);
    try {
      await this.addVariants([{ id: Number(item.variantId), quantity: 1 }]);
      button.textContent = 'Added';
      this.toast('Added to cart');
      setTimeout(() => {
        button.textContent = oldLabel;
        button.disabled = item.available === false;
      }, 900);
    } catch (error) {
      this.setItemError(productId, error.message || 'Could not add to cart');
      button.textContent = oldLabel;
      button.disabled = false;
    }
  }

  async addAll(button) {
    const valid = this.items.filter((item) => item.variantId && item.available !== false);
    if (!valid.length) return;
    button.disabled = true;
    const oldLabel = button.textContent;
    button.textContent = 'Adding…';
    try {
      await this.addVariants(valid.map((item) => ({ id: Number(item.variantId), quantity: 1 })));
      button.textContent = 'Added to cart';
      this.toast(`${valid.length} item${valid.length === 1 ? '' : 's'} added to cart`);
      setTimeout(() => {
        button.textContent = oldLabel;
        button.disabled = false;
      }, 1000);
    } catch (error) {
      button.textContent = oldLabel;
      button.disabled = false;
      this.toast(error.message || 'Could not add all items');
    }
  }

  createNativeCartUpdate(items) {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (!cartItemsComponent || typeof cartItemsComponent.fetchCartData !== 'function') return null;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const lines = items.map((item) => ({
      merchandiseId: String(item.id),
      quantity: Number(item.quantity) || 1,
    }));
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);

    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'product',
      lines,
      promise: deferredEventPromise.promise,
    });

    const dispatchTarget = this.drawer || document.querySelector('[data-khushi-wishlist-open]') || document.body;
    dispatchTarget.dispatchEvent(event);

    return { cartItemsComponent, deferredEventPromise, itemCount };
  }

  async addVariants(items) {
    const normalizedItems = items.filter((item) => item?.id && Number(item.quantity || 0) > 0).map((item) => ({ id: Number(item.id), quantity: Number(item.quantity) || 1 }));
    if (!normalizedItems.length) throw new Error('No available wishlist items to add');

    const nativeUpdate = this.createNativeCartUpdate(normalizedItems);
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: normalizedItems }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');

      if (nativeUpdate) {
        const ajaxCart = await nativeUpdate.cartItemsComponent.fetchCartData();
        nativeUpdate.deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'khushi-wishlist',
            sourceId: 'khushi-wishlist',
            itemCount: nativeUpdate.itemCount,
            sections: data.sections || ajaxCart?.sections,
            didError: false,
          },
        });
      }

      window.dispatchEvent(new CustomEvent('khushi:wishlist:cart-updated'));
      return data;
    } catch (error) {
      nativeUpdate?.deferredEventPromise.reject(error);
      throw error;
    }
  }

  setItemError(productId, message) {
    const row = this.drawer?.querySelector(`[data-wishlist-product="${CSS.escape(String(productId))}"]`);
    const element = row?.querySelector('[data-khushi-error]');
    if (element) {
      element.hidden = false;
      element.textContent = message;
    }
  }

  clearItemError(productId) {
    const row = this.drawer?.querySelector(`[data-wishlist-product="${CSS.escape(String(productId))}"]`);
    const element = row?.querySelector('[data-khushi-error]');
    if (element) {
      element.hidden = true;
      element.textContent = '';
    }
  }

  toast(message) {
    let toast = document.querySelector('.khushi-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'khushi-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
  }
}

const start = () => {
  if (!window.khushiWishlist) window.khushiWishlist = new KhushiWishlist();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
